/**
 * The `yieldCurve` section — the yield curve, linked to markets and the economy
 * (`docs/plans/macro.md`; behaviour spec `docs/mockups/macro-v2.html`, section 02).
 *
 * **Left:** the curve at the cursor with a pinned comparison (1M / 1Y / 2Y
 * earlier, or this week), the bp table, readout tiles, two spread strips with
 * pickers and the tenor heat strip. **Right:** linked charts (SPY vs 10Y first),
 * each with its own paired rate on a left axis, ⇅ to flip it, a last-value tag,
 * the change since the pin and ρ52w.
 *
 * **One cursor.** Hover any strip or chart and every panel shows that week (the
 * curve draws it as a ghost); drag scrubs; ▶ Play draws every panel up to the
 * cursor, and with Follow 2Y / 5Y the view scrolls along with it, keeping a
 * quarter of the window ahead in view — faint, or hidden. Cursor, hover,
 * playback, speed, spreads, pairs and flips are view state (D8); the window,
 * the pin and the lanes are the section's config.
 *
 * **The cursor is published** for the rest of the layout through
 * `lib/macro-cursor.ts` (week + playing): this section is its one writer, and any
 * other section may read it with `useMacroCursor()`.
 *
 * The pure half is `lib/yield-curve.ts`, the data `api/macro-curve.ts`. Colours
 * are theme tokens through `useTokenResolver` (D13) — SVG and canvas need real
 * values, so nothing here is a hex.
 *
 * Replace the body, keep the signature: the registry imports this default export.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent, ReactNode } from "react";
import type { YieldCurveConfig } from "@qufolio/model";
import type { MacroSectionProps } from "./types";
import { useWidth } from "./use-width";
import { useTokenResolver } from "../theme";
import { usePublishMacroCursor } from "../../lib/macro-cursor";
import { macroScale } from "../../lib/macro-sections";
import {
  fetchCurve,
  fetchFedFunds,
  fetchWeeklyBars,
  type DataSource,
  type StepAnswer,
} from "../../api/macro-curve";
import {
  DEFAULT_SPREADS,
  HEAT_MAX,
  LANES,
  PAIRS,
  SPREADS,
  TENORS,
  alignBars,
  alignSteps,
  bpChange,
  eventIndices,
  eventsNear,
  finiteRange,
  heatColor,
  indexAtX,
  invertedStreak,
  isInverted,
  isoDay,
  lanesOf,
  pairAt,
  pinIndex,
  playIntervalMs,
  playStep,
  rho52,
  signed,
  sinceChange,
  spreadAt,
  tenorIndex,
  timeTicks,
  toggleLane,
  viewDomain,
  weekly,
  withAlpha,
  type CurveWeek,
  type LaneDef,
  type PairKey,
  type PinSetting,
  type SpreadKey,
  type WeeklyBar,
  type WindowSetting, fedRange } from "../../lib/yield-curve";

type Ahead = "faint" | "hide";
/** Per symbol: being asked for, or asked and not answered (error, empty or timeout). */
type BarStatus = "loading" | "failed";
type Resolve = (token: string) => string;

/** The app's own font (Inter), not a monospace (owner, 2026-11-02); numbers line up via tabular-nums on the host. */
const MONO = "inherit";

/** What every time-axis panel shares: the clock, the cursor, and how a pointer moves it. */
interface Clock {
  weeks: CurveWeek[];
  last: number;
  lo: number;
  hi: number;
  cursor: number;
  hover: number | null;
  pin: number | null;
  ahead: Ahead;
  shown: (i: number) => boolean;
  link: (left: number, right: number) => LinkHandlers;
}

interface LinkHandlers {
  onPointerDown: (e: PointerEvent<Element>) => void;
  onPointerMove: (e: PointerEvent<Element>) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  style: CSSProperties;
}

export default function YieldCurveSection({ section, config, onConfig }: MacroSectionProps<"yieldCurve">) {
  const r = useTokenResolver();

  // Config: persisted through onConfig, or held here when the section is read-only.
  const [own, setOwn] = useState<YieldCurveConfig | null>(null);
  const cfg = onConfig ? config : (own ?? config);
  const change = (next: YieldCurveConfig) => (onConfig ? onConfig(next) : setOwn(next));
  const win: WindowSetting = cfg.window ?? "5y";
  const pinSetting: PinSetting = cfg.pin ?? "1y";
  const laneKeys = lanesOf(cfg);
  // The stacked layout's drag-to-resize: every chart height times this
  // (`lib/macro-sections.ts`). Read from `config`, which carries the live value
  // mid-drag; widths stay measured, so text is never scaled.
  const scale = macroScale(config);

  // Data.
  const [curveSource, setCurveSource] = useState<DataSource | null>(null);
  const [weeks, setWeeks] = useState<CurveWeek[]>([]);
  const [fed, setFed] = useState<StepAnswer | null>(null);
  const [bars, setBars] = useState<Record<string, WeeklyBar[]>>({});
  const [barsSource, setBarsSource] = useState<DataSource | null>(null);
  const [barStatus, setBarStatus] = useState<Record<string, BarStatus>>({});
  const [retryTick, setRetryTick] = useState(0);
  /** Symbols asked for and not failed — in flight or answered. */
  const asked = useRef(new Set<string>());

  useEffect(() => {
    let live = true;
    void Promise.all([fetchCurve(), fetchFedFunds()]).then(([c, f]) => {
      if (!live) return;
      setWeeks(weekly(c.rows));
      setCurveSource(c.source);
      setFed(f);
    });
    return () => {
      live = false;
    };
  }, []);

  // **No per-run `live` flag on this one.** A run's answer is good whatever has
  // happened to the effect since — a symbol's bars do not depend on which other
  // lanes are on — and dropping it is the bug: under StrictMode the first run is
  // cleaned up at once, its fetch was the only one (the second run found every
  // symbol already asked), and the lanes said "Loading bars…" for ever; a lane
  // switched on mid-flight did the same to the lanes already in flight. So an
  // answer is always merged, and a symbol that came back with nothing leaves
  // `asked` — failed, retryable by ↻ or by switching its lane off and on.
  const pxKeys = laneKeys.filter((k) => LANES.find((l) => l.key === k)?.kind === "px").join(",");
  useEffect(() => {
    const want = pxKeys ? pxKeys.split(",").filter((k) => !asked.current.has(k)) : [];
    if (want.length === 0) return;
    want.forEach((k) => asked.current.add(k));
    setBarStatus((s) => ({ ...s, ...Object.fromEntries(want.map((k) => [k, "loading" as const])) }));
    const settle = (got: Record<string, WeeklyBar[]>, source: DataSource | null) => {
      const ok = want.filter((k) => (got[k]?.length ?? 0) > 0);
      want.forEach((k) => {
        if (!ok.includes(k)) asked.current.delete(k);
      });
      if (ok.length) setBars((b) => ({ ...b, ...Object.fromEntries(ok.map((k) => [k, got[k]!])) }));
      setBarStatus((s) => {
        const n = { ...s };
        for (const k of want) {
          if (ok.includes(k)) delete n[k];
          else n[k] = "failed";
        }
        return n;
      });
      if (source) setBarsSource(source);
    };
    fetchWeeklyBars(want).then(
      (a) => settle(a.bars, a.source),
      () => settle({}, null),
    );
  }, [pxKeys, retryTick]);
  const retryBars = (k: string) => {
    asked.current.delete(k);
    setBarStatus((s) => {
      const n = { ...s };
      delete n[k];
      return n;
    });
    setRetryTick((t) => t + 1);
  };

  // View state (D8): never stored.
  const last = weeks.length - 1;
  const [cursorState, setCursor] = useState<number | null>(null);
  const cursor = Math.max(0, Math.min(last, cursorState ?? last));
  const [hover, setHover] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const [ahead, setAhead] = useState<Ahead>("faint");
  const [pinState, setPinState] = useState<number | null | undefined>(undefined);
  const pin = last < 0 ? null : pinState === undefined ? pinIndex(pinSetting, last) : pinState;
  const [spreads, setSpreads] = useState<[SpreadKey, SpreadKey]>([...DEFAULT_SPREADS]);
  const [overrides, setOverrides] = useState<Record<string, { pair?: PairKey; inverted?: boolean }>>({});

  // The layout's cursor (`lib/macro-cursor.ts`): this section is its one writer.
  usePublishMacroCursor(section.id, last < 0 ? null : (weeks[cursor]?.t ?? null), playing);

  // Playback.
  useEffect(() => {
    if (!playing || last < 0) return;
    const id = setInterval(() => {
      setCursor((c) => {
        const next = playStep(c ?? last, last);
        return next ?? last;
      });
    }, playIntervalMs(speed));
    return () => clearInterval(id);
  }, [playing, speed, last]);
  useEffect(() => {
    if (playing && cursor >= last) setPlaying(false);
  }, [playing, cursor, last]);

  const play = () => {
    if (playing) return setPlaying(false);
    if (cursor >= last) setCursor(0);
    setHover(null);
    setPlaying(true);
  };
  const step = (d: number) => {
    setPlaying(false);
    setCursor(Math.max(0, Math.min(last, cursor + d)));
  };
  const setPin = (p: PinSetting) => {
    setPinState(pinIndex(p, cursor));
    change({ ...cfg, pin: p });
  };

  const { lo, hi } = viewDomain(win, cursor, Math.max(0, last));
  const shown = useCallback((i: number) => ahead !== "hide" || i <= cursor, [ahead, cursor]);
  const dragging = useRef(false);
  const link = (left: number, right: number): LinkHandlers => {
    const at = (e: PointerEvent<Element>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return indexAtX(e.clientX - rect.left, left, right, lo, hi, last);
    };
    return {
      onPointerDown: (e) => {
        dragging.current = true;
        setPlaying(false);
        (e.currentTarget as Element & { setPointerCapture?: (id: number) => void }).setPointerCapture?.(e.pointerId);
        setCursor(at(e));
        setHover(null);
      },
      onPointerMove: (e) => {
        const i = at(e);
        if (dragging.current) {
          setCursor(i);
          setHover(null);
        } else setHover(i);
      },
      onPointerUp: () => {
        dragging.current = false;
      },
      onPointerLeave: () => {
        if (!dragging.current) setHover(null);
      },
      style: { cursor: "crosshair", touchAction: "none", display: "block" },
    };
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("input,textarea,select")) return;
    if (e.key === "ArrowLeft") step(-1);
    else if (e.key === "ArrowRight") step(1);
    else if (e.key === " ") {
      e.preventDefault();
      play();
    }
  };

  const [rootRef, rootWidth] = useWidth(1200);
  const twoCols = rootWidth >= 1000;

  // Lanes, aligned onto the weeks once per data change.
  const fedWeekly = useMemo(() => (fed ? alignSteps(weeks, fed.steps) : []), [weeks, fed]);
  const aligned = useMemo(() => {
    const out: Record<string, ReturnType<typeof alignBars>> = {};
    for (const [k, b] of Object.entries(bars)) out[k] = alignBars(weeks, b);
    return out;
  }, [weeks, bars]);

  const clock: Clock = { weeks, last, lo, hi, cursor, hover: hover != null && hover !== cursor ? hover : null, pin, ahead, shown, link };

  if (curveSource == null) {
    return (
      <div data-macro-kind="yieldCurve" style={{ ...S.root, alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--qf-text-muted)" }}>Loading the curve…</span>
      </div>
    );
  }
  if (last < 0) {
    return (
      <div data-macro-kind="yieldCurve" style={{ ...S.root, alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--qf-text-muted)" }}>No curve history.</span>
      </div>
    );
  }

  const cur = weeks[cursor]!;
  const inv = isInverted(cur);

  return (
    <div
      ref={rootRef}
      data-macro-kind="yieldCurve"
      tabIndex={0}
      onKeyDown={onKey}
      aria-label={section.title ?? "Yield curve, linked"}
      style={S.root}
    >
      <div style={S.head}>
        <button type="button" style={{ ...S.pill, color: "var(--qf-accent)", minWidth: 72 }} onClick={play}>
          {playing ? "❚❚ Pause" : "▶ Play"}
        </button>
        <button type="button" style={S.pill} aria-label="One week back" onClick={() => step(-1)}>
          ◀
        </button>
        <button type="button" style={S.pill} aria-label="One week forward" onClick={() => step(1)}>
          ▶
        </button>
        <span style={S.lbl}>Speed</span>
        <Seg
          label="Speed"
          value={String(speed)}
          options={[
            ["1", "1×"],
            ["4", "4×"],
            ["12", "12×"],
          ]}
          onPick={(v) => setSpeed(Number(v))}
        />
        <span style={S.lbl}>Window</span>
        <Seg
          label="Window"
          value={win}
          options={[
            ["2y", "Follow 2Y"],
            ["5y", "Follow 5Y"],
            ["all", "All"],
          ]}
          onPick={(v) => change({ ...cfg, window: v as WindowSetting })}
        />
        <span style={S.lbl}>📌 Pin</span>
        <Seg
          label="Pin"
          value={pinSetting}
          options={[
            ["1m", "1M earlier"],
            ["1y", "1Y earlier"],
            ["2y", "2Y earlier"],
            ["thisWeek", "This week"],
            ["off", "✕"],
          ]}
          onPick={(v) => setPin(v as PinSetting)}
        />
        <span style={S.lbl}>Ahead</span>
        <Seg
          label="Ahead of the cursor"
          value={ahead}
          options={[
            ["faint", "Faint"],
            ["hide", "Hidden"],
          ]}
          onPick={(v) => setAhead(v as Ahead)}
        />
        <span style={{ flex: 1 }} />
        {curveSource === "fixture" && <Badge title="The macro series are not built on this server yet (or could not be read); drawn from fixture data">curve: fixture</Badge>}
        <span
          data-testid="yc-state"
          style={{ ...S.pill, color: inv ? "var(--qf-negative)" : "var(--qf-positive)", borderColor: "currentColor" }}
        >
          {inv ? "Inverted · 10Y < 2Y" : "Normal"}
        </span>
      </div>

      <div style={{ ...S.grid, gridTemplateColumns: twoCols ? "minmax(0,1fr) minmax(0,1.15fr)" : "minmax(0,1fr)" }}>
        <div style={S.col}>
          {/* The readout is one row over the curve, and the tenor table sits under
              the heat strip, so the curve stands right over its spreads
              (owner, 2026-11-01). */}
          <ReadTiles clock={clock} spreads={spreads} fedUpper={fedWeekly} />
          <div style={S.box}>
            <CurveChart r={r} clock={clock} playing={playing} scale={scale} />
          </div>
          <div style={S.box}>
            {[0, 1].map((n) => (
              <SpreadStrip
                key={n}
                n={n}
                r={r}
                clock={clock}
                spread={spreads[n]!}
                scale={scale}
                onSpread={(k) => setSpreads((s) => (n === 0 ? [k, s[1]] : [s[0], k]))}
              />
            ))}
            <HeatStrip r={r} clock={clock} scale={scale} />
          </div>
          <BpTable clock={clock} />
        </div>
        <div style={S.col}>
          <div style={S.box}>
            <div style={{ ...S.sphd, justifyContent: "flex-start", flexWrap: "wrap" }}>
              <b style={{ fontSize: 13.5 }}>Markets &amp; economy</b>
              <span style={{ color: "var(--qf-text-muted)" }}>linked · same clock</span>
              {barsSource === "fixture" && <Badge title="No chart socket configured; bars are fixture data">bars: fixture</Badge>}
            </div>
            <div style={{ ...S.sphd, justifyContent: "flex-start" }}>
              <div role="group" aria-label="Linked charts" style={{ ...S.seg, flexWrap: "wrap" }}>
                {LANES.map((l) => {
                  const on = laneKeys.includes(l.key);
                  return (
                    <button
                      key={l.key}
                      type="button"
                      aria-pressed={on}
                      style={{ ...S.segBtn, ...(on ? S.segOn : null) }}
                      onClick={() => change({ ...cfg, lanes: toggleLane(laneKeys, l.key) })}
                    >
                      <span style={{ color: r(l.color) }}>●</span> {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {laneKeys.length === 0 && <div style={{ ...S.sphd, color: "var(--qf-text-muted)" }}>Pick a chart above.</div>}
            {laneKeys.map((k, i) => {
              const def = LANES.find((l) => l.key === k)!;
              const o = overrides[k] ?? {};
              const lane: LaneDef = { ...def, pair: o.pair ?? def.pair, inverted: o.inverted ?? def.inverted };
              const values = lane.kind === "px" ? aligned[k]?.close : k === "FEDFUNDS" ? fedWeekly : undefined;
              return (
                <Lane
                  key={k}
                  r={r}
                  clock={clock}
                  lane={lane}
                  bars={lane.kind === "px" ? aligned[k] : undefined}
                  values={values}
                  status={lane.kind !== "px" || aligned[k] ? "ok" : barStatus[k] === "failed" ? "failed" : "loading"}
                  onRetry={() => retryBars(k)}
                  fixture={k === "FEDFUNDS" ? fed?.source === "fixture" : false}
                  isLast={i === laneKeys.length - 1}
                  scale={scale}
                  onPair={(p) => setOverrides((s) => ({ ...s, [k]: { ...s[k], pair: p } }))}
                  onFlip={() => setOverrides((s) => ({ ...s, [k]: { ...s[k], inverted: !lane.inverted } }))}
                />
              );
            })}
          </div>
        </div>
      </div>
      <Legend r={r} />
    </div>
  );
}

// ── The curve ───────────────────────────────────────────────────────────────

function CurveChart({ r, clock, playing, scale }: { r: Resolve; clock: Clock; playing: boolean; scale: number }) {
  const { weeks, cursor, pin, hover } = clock;
  const W = 700;
  const H = Math.round(300 * scale);
  const L = 40;
  const R = 14;
  const T = 14;
  const B = 28;
  const cur = weeks[cursor]!;
  const pinned = pin != null && pin !== cursor ? weeks[pin] : undefined;
  const ghost = hover != null ? weeks[hover] : undefined;
  const drawn = [cur, pinned, ghost].filter((w): w is CurveWeek => !!w).flatMap((w) => w.y.filter(Number.isFinite));
  const hiV = Math.max(6, Math.ceil(Math.max(...drawn, 0)));
  const loV = Math.min(0, Math.floor(Math.min(...drawn, 0)));
  const stepV = hiV - loV > 10 ? 2 : 1;
  const x = (i: number) => L + (i * (W - L - R)) / (TENORS.length - 1);
  const y = (v: number) => T + ((hiV - v) / (hiV - loV)) * (H - T - B);
  const path = (a: readonly number[]) => {
    let d = "";
    let pen = false;
    a.forEach((v, i) => {
      if (!Number.isFinite(v)) {
        pen = false;
        return;
      }
      d += (pen ? "L" : "M") + x(i).toFixed(1) + "," + y(v).toFixed(1);
      pen = true;
    });
    return d;
  };
  const inv = isInverted(cur);
  const col = r(inv ? "negative" : "accent");
  const levels: number[] = [];
  for (let v = loV; v <= hiV; v += stepV) levels.push(v);
  const i2 = tenorIndex("2Y");
  const i10 = tenorIndex("10Y");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label={`Yield curve, week of ${isoDay(cur.t)}`}>
      {levels.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={r("grid")} />
          <text x={L - 6} y={y(v) + 3} textAnchor="end" style={txt(r)}>
            {v}%
          </text>
        </g>
      ))}
      {TENORS.map((t, i) => (
        <text key={t} x={x(i)} y={H - 10} textAnchor="middle" style={txt(r)}>
          {t}
        </text>
      ))}
      <text x={W - R - 6} y={T + 40} textAnchor="end" style={{ ...txt(r), fontSize: 35, fontWeight: 600, fill: r("axis") }}>
        {isoDay(cur.t).slice(0, 7)}
      </text>
      {pinned && (
        <>
          <path d={path(pinned.y)} fill="none" stroke={r("warn")} strokeWidth={1.5} strokeDasharray="5 4" />
          <text x={W - R - 6} y={T + 58} textAnchor="end" style={{ ...txt(r), fill: r("warn") }}>
            📌 {isoDay(pinned.t)}
          </text>
        </>
      )}
      {playing &&
        [8, 7, 6, 5, 4, 3, 2, 1].map((k) => {
          const w = weeks[cursor - k];
          return w ? (
            <path key={k} d={path(w.y)} fill="none" stroke={r("text.muted")} strokeWidth={1} strokeOpacity={0.55 * (1 - k / 9)} />
          ) : null;
        })}
      {ghost && (
        <>
          <path d={path(ghost.y)} fill="none" stroke={r("text")} strokeWidth={1.3} strokeOpacity={0.75} strokeDasharray="2 2" />
          <text x={W - R - 6} y={T + 74} textAnchor="end" style={{ ...txt(r), fill: r("text") }}>
            hover {isoDay(ghost.t)}
          </text>
        </>
      )}
      <path d={path(cur.y)} fill="none" stroke={col} strokeWidth={3.4} />
      {cur.y.map((v, i) =>
        Number.isFinite(v) ? <circle key={i} cx={x(i)} cy={y(v)} r={i === i2 || i === i10 ? 4 : 2.5} fill={col} /> : null,
      )}
      {[i2, i10].map((i) =>
        Number.isFinite(cur.y[i]!) ? (
          <text key={i} x={x(i)} y={y(cur.y[i]!) - 9} textAnchor="middle" style={{ ...txt(r), fill: col }}>
            {cur.y[i]!.toFixed(2)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/**
 * The 1M bill beside the Fed's target range (owner, 2026-11-02), so a bill
 * trading above the range reads as information — bills are quoted bond-equivalent
 * and cheapen with supply — rather than as a data error.
 */
function FedTile({ oneMonth, upper }: { oneMonth: number | null | undefined; upper: number | null | undefined }) {
  const r = fedRange(upper);
  const m = oneMonth != null && Number.isFinite(oneMonth) ? oneMonth : null;
  const above = r && m != null && m > r.hi;
  const below = r && m != null && m < r.lo;
  return (
    <Tile
      k="1M · Fed"
      v={`${m == null ? "—" : m.toFixed(2)} · ${r ? r.label : "—"}`}
      sub={above ? "1M above the range" : below ? "1M below the range" : undefined}
    />
  );
}

function ReadTiles({
  clock, spreads, fedUpper,
}: {
  clock: Clock;
  spreads: [SpreadKey, SpreadKey];
  /** The Fed's target, upper bound, per week of the clock (`fedWeekly`); empty when unknown. */
  fedUpper?: readonly (number | null | undefined)[];
}) {
  const { weeks, cursor, pin } = clock;
  const f = weeks[cursor]!;
  const p = pin != null && pin !== cursor ? weeks[pin] : undefined;
  const ev = eventsNear(f.t);
  const streak = invertedStreak(weeks, cursor, spreads[0]);
  const tenor = (t: "2Y" | "10Y") => f.y[tenorIndex(t)]!;
  return (
    <div style={S.tiles} data-testid="yc-tiles">
      <Tile k="Week of" v={isoDay(f.t)} sub={ev.length ? "◆ " + ev.join(" · ") : undefined} />
      <FedTile oneMonth={f.y[tenorIndex("1M")]} upper={fedUpper?.[cursor]} />
      <Tile k="2Y · 10Y" v={`${tenor("2Y").toFixed(2)} · ${tenor("10Y").toFixed(2)}`} />
      {spreads.map((k) => {
        const v = spreadAt(f, k);
        return (
          <Tile
            key={k}
            k={k}
            v={signed(v)}
            tone={v < 0 ? "neg" : "pos"}
            sub={p ? "📌 " + signed(spreadAt(p, k)) : undefined}
          />
        );
      })}
      <Tile k={`${spreads[0]} inverted`} v={streak ? `${streak} wks` : "—"} tone={streak ? "neg" : "dim"} />
    </div>
  );
}

function Tile({ k, v, sub, tone }: { k: string; v: string; sub?: string | undefined; tone?: "pos" | "neg" | "dim" }) {
  const color =
    tone === "pos" ? "var(--qf-positive)" : tone === "neg" ? "var(--qf-negative)" : tone === "dim" ? "var(--qf-text-muted)" : "var(--qf-text)";
  return (
    <div style={S.tile}>
      <div style={S.tileK}>{k}</div>
      <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color }}>{v}</div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 11.5, color: "var(--qf-warn)" }}>{sub}</div>}
    </div>
  );
}

function BpTable({ clock }: { clock: Clock }) {
  const { weeks, cursor, pin } = clock;
  const f = weeks[cursor]!;
  const p = pin != null && pin !== cursor ? weeks[pin] : undefined;
  const bp = p ? bpChange(f, p) : null;
  const num = (v: number) => (Number.isFinite(v) ? v.toFixed(2) : "—");
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={S.tbl} data-testid="yc-bp">
        <thead>
          <tr>
            <th style={S.th} />
            {TENORS.map((t) => (
              <th key={t} style={{ ...S.th, textAlign: "right" }}>
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...S.td, color: "var(--qf-text-muted)", whiteSpace: "nowrap" }}>{isoDay(f.t)}</td>
            {f.y.map((v, i) => (
              <td key={i} style={S.tdR}>
                {num(v)}
              </td>
            ))}
          </tr>
          {p && bp && (
            <>
              <tr>
                <td style={{ ...S.td, color: "var(--qf-warn)", whiteSpace: "nowrap" }}>📌 {isoDay(p.t)}</td>
                {p.y.map((v, i) => (
                  <td key={i} style={{ ...S.tdR, color: "var(--qf-text-muted)" }}>
                    {num(v)}
                  </td>
                ))}
              </tr>
              <tr>
                <td style={{ ...S.td, color: "var(--qf-text-muted)" }}>Δ bp</td>
                {bp.map((d, i) => (
                  <td
                    key={i}
                    style={{
                      ...S.tdR,
                      color: !Number.isFinite(d) || d === 0 ? "var(--qf-text-muted)" : d < 0 ? "var(--qf-positive)" : "var(--qf-negative)",
                    }}
                  >
                    {Number.isFinite(d) ? (d > 0 ? "+" : "") + d : "—"}
                  </td>
                ))}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Time-axis panels ────────────────────────────────────────────────────────

/** Gridlines + labels, the pin, hover and cursor lines: every time-axis panel draws these. */
function TimeMarks({
  r,
  clock,
  x,
  top,
  bottom,
  labels,
  part,
}: {
  r: Resolve;
  clock: Clock;
  x: (i: number) => number;
  top: number;
  bottom: number;
  labels: boolean;
  /** `grid` under the data, `marks` (pin, hover, cursor) over it. */
  part: "grid" | "marks";
}) {
  const { weeks, lo, hi, pin, hover, cursor } = clock;
  const ticks = useMemo(() => timeTicks(weeks, lo, hi), [weeks, lo, hi]);
  if (part === "grid") {
    return (
      <>
        {ticks.map((t) => (
        <g key={t.index}>
          <line x1={x(t.index)} x2={x(t.index)} y1={top} y2={bottom} stroke={r("grid")} />
          {labels && (
            <text x={x(t.index) + 3} y={bottom + 11} style={{ ...txt(r), fontSize: 10, fill: r(t.year ? "text.secondary" : "text.muted") }}>
              {t.label}
            </text>
          )}
        </g>
        ))}
      </>
    );
  }
  return (
    <>
      {pin != null && pin >= lo && pin <= hi && (
        <line x1={x(pin)} x2={x(pin)} y1={top} y2={bottom} stroke={r("warn")} strokeDasharray="3 3" />
      )}
      {hover != null && hover >= lo && hover <= hi && (
        <line x1={x(hover)} x2={x(hover)} y1={top} y2={bottom} stroke={r("text")} strokeOpacity={0.5} />
      )}
      <line x1={x(cursor)} x2={x(cursor)} y1={top} y2={bottom} stroke={r("text")} strokeWidth={1.5} />
    </>
  );
}

function Bubble({ r, x, y, text, color, right }: { r: Resolve; x: number; y: number; text: string; color?: string; right: number }) {
  const w = text.length * 6.2 + 8;
  const bx = x + 6 + w > right ? x - 6 - w : x + 6;
  return (
    <g pointerEvents="none">
      <rect x={bx} y={y - 8} width={w} height={15} rx={3} fill={r("surface")} stroke={color ?? r("border")} />
      <text x={bx + 4} y={y + 3} style={{ ...txt(r), fill: r("text") }}>
        {text}
      </text>
    </g>
  );
}

function linePath(values: readonly number[], a: number, b: number, x: (i: number) => number, y: (v: number) => number): string {
  let d = "";
  let pen = false;
  for (let i = a; i <= b; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) {
      pen = false;
      continue;
    }
    d += (pen ? "L" : "M") + x(i).toFixed(1) + "," + y(v).toFixed(1);
    pen = true;
  }
  return d;
}

function SpreadStrip({
  n,
  r,
  clock,
  spread,
  onSpread,
  scale,
}: {
  n: number;
  r: Resolve;
  clock: Clock;
  spread: SpreadKey;
  onSpread: (k: SpreadKey) => void;
  scale: number;
}) {
  const [ref, W] = useWidth(600);
  const { weeks, lo, hi, cursor, pin, hover, shown, ahead } = clock;
  const H = Math.round(96 * scale);
  const L = 0;
  const R = W - 44;
  const T = 10;
  const B = 14;
  const values = useMemo(() => weeks.map((w) => spreadAt(w, spread)), [weeks, spread]);
  const end = Math.min(cursor, hi);
  const range = finiteRange(values, lo, ahead === "hide" ? Math.min(hi, cursor) : hi) ?? [0, 0];
  const vlo = Math.min(range[0], 0) - 0.1;
  const vhi = Math.max(range[1], 0) + 0.1;
  const x = (i: number) => L + ((i - lo) * (R - L)) / Math.max(1, hi - lo);
  const y = (v: number) => T + ((vhi - v) / (vhi - vlo)) * (H - T - B);
  const area = (neg: boolean) => {
    if (end < lo) return "";
    let d = `M${x(lo)},${y(0)}`;
    for (let i = lo; i <= end; i++) {
      const v = values[i]!;
      const c = Number.isFinite(v) ? (neg ? Math.min(v, 0) : Math.max(v, 0)) : 0;
      d += `L${x(i).toFixed(1)},${y(c).toFixed(1)}`;
    }
    return d + `L${x(end)},${y(0)}Z`;
  };
  const events = useMemo(() => (n === 0 ? eventIndices(weeks) : []), [n, weeks]);
  const v = values[cursor]!;
  const pv = pin != null && pin !== cursor ? values[pin] : undefined;
  return (
    <div style={n ? { borderTop: "1px solid var(--qf-border)" } : undefined}>
      <div style={S.sphd}>
        <select aria-label={`Spread ${n + 1}`} value={spread} onChange={(e) => onSpread(e.target.value as SpreadKey)} style={S.select}>
          {Object.keys(SPREADS).map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <span>
          <b style={{ color: v < 0 ? "var(--qf-negative)" : "var(--qf-positive)" }}>{signed(v)}</b>
          {pv != null && <span style={{ color: "var(--qf-warn)" }}> 📌 {signed(pv)}</span>}
        </span>
      </div>
      <div ref={ref}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} {...clock.link(L, R)}>
          <TimeMarks r={r} clock={clock} x={x} top={T} bottom={H - B} labels part="grid" />
          <line x1={L} x2={R} y1={y(0)} y2={y(0)} stroke={r("axis")} />
          {[vhi - 0.1, 0, vlo + 0.1].map((t, i) => (
            <text key={i} x={R + 4} y={y(t) + 3} style={txt(r)}>
              {signed(t)}
            </text>
          ))}
          <path d={area(true)} fill={withAlpha(r("negative"), 0.3)} />
          <path d={area(false)} fill={withAlpha(r("positive"), 0.18)} />
          <path d={linePath(values, lo, end, x, y)} fill="none" stroke={r("series5")} strokeWidth={2.3} />
          {ahead === "faint" && cursor < hi && (
            <path d={linePath(values, Math.max(lo, cursor), hi, x, y)} fill="none" stroke={r("text.muted")} strokeOpacity={0.45} strokeWidth={2.3} />
          )}
          {events
            .filter((e) => e.index >= lo && e.index <= hi && shown(e.index))
            .map((e) => (
              <g key={e.date}>
                <path d={`M${x(e.index)},${T - 8}l4,5l-4,5l-4,-5z`} fill={r("warn")} />
                <title>{`${e.date} · ${e.name}`}</title>
              </g>
            ))}
          <TimeMarks r={r} clock={clock} x={x} top={0} bottom={H - B} labels={false} part="marks" />
          {hover != null && hover >= lo && hover <= hi && Number.isFinite(values[hover]!) && (
            <Bubble r={r} x={x(hover)} y={y(values[hover]!)} text={signed(values[hover]!)} right={R} />
          )}
        </svg>
      </div>
    </div>
  );
}

function HeatStrip({ r, clock, scale }: { r: Resolve; clock: Clock; scale: number }) {
  const [ref, W] = useWidth(600);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const { weeks, lo, hi, cursor, pin, hover, ahead } = clock;
  const H = Math.round(48 * scale);
  const L = 0;
  const R = W - 44;
  const ramp: [string, string, string] = [r("accent.blue"), r("warn"), r("negative")];
  const bg = r("background");
  const surface = r("surface");
  const muted = r("text.muted");
  const text = r("text");
  const warn = r("warn");
  // A fixed 0–6% ramp, as the mock draws it: scaled to the all-history max, the
  // 1981 peak (15%+) would wash every week since 2008 into the bottom third.
  const top = HEAT_MAX;
  useEffect(() => {
    const cv = canvas.current;
    const g = cv?.getContext?.("2d");
    if (!cv || !g) return;
    if (cv.width !== W) cv.width = W;
    const x = (i: number) => L + ((i - lo) * (R - L)) / Math.max(1, hi - lo);
    const rh = H / TENORS.length;
    const cw = (R - L) / Math.max(1, hi - lo);
    g.clearRect(0, 0, W, H);
    for (let i = lo; i <= hi; i++) {
      weeks[i]!.y.forEach((v, k) => {
        g.fillStyle = heatColor(v, top, ramp);
        g.fillRect(x(i) - cw / 2, H - (k + 1) * rh, cw + 1, rh + 0.5);
      });
    }
    g.fillStyle = withAlpha(bg, ahead === "hide" ? 0.9 : 0.55);
    g.fillRect(x(cursor) + cw / 2, 0, R - x(cursor), H);
    g.fillStyle = surface;
    g.fillRect(R, 0, W - R, H);
    g.fillStyle = muted;
    g.font = `10px ${MONO}`;
    g.fillText(TENORS[TENORS.length - 1]!, R + 4, 10);
    g.fillText(TENORS[0]!, R + 4, H - 3);
    if (pin != null && pin >= lo && pin <= hi) {
      g.fillStyle = warn;
      g.fillRect(x(pin) - 1, 0, 2, H);
    }
    if (hover != null && hover >= lo && hover <= hi) {
      g.fillStyle = withAlpha(text, 0.6);
      g.fillRect(x(hover) - 0.5, 0, 1, H);
    }
    g.fillStyle = text;
    g.fillRect(x(cursor) - 1, 0, 2, H);
    // `ramp` is rebuilt per render from the same tokens; its members are the deps.
  }, [W, R, weeks, lo, hi, cursor, pin, hover, ahead, top, ramp[0], ramp[1], ramp[2], bg, surface, muted, text, warn]);
  return (
    <div ref={ref} style={{ borderTop: "1px solid var(--qf-border)" }}>
      <canvas
        ref={canvas}
        width={W}
        height={H}
        aria-label="Tenor heat strip, 1M at the bottom to 30Y at the top"
        {...clock.link(L, R)}
        style={{ ...clock.link(L, R).style, width: "100%", height: H }}
      />
    </div>
  );
}

// ── Linked charts ───────────────────────────────────────────────────────────
//
// Hand-drawn SVG, not `@qufolio/chart-engine`, until the engine can replace a
// series' data (draw to a cursor that steps back), flip a user Y axis (⇅), draw
// host-driven cursor and pin lines, and turn plot-area pan off for the scrub.
// The four gaps, exactly: `docs/TODO.md`, *Charts*.

function Lane({
  r,
  clock,
  lane,
  bars,
  values,
  status,
  onRetry,
  fixture,
  isLast,
  onPair,
  onFlip,
  scale,
}: {
  r: Resolve;
  clock: Clock;
  lane: LaneDef;
  bars: ReturnType<typeof alignBars> | undefined;
  values: number[] | undefined;
  /** `loading` until a px lane's bars arrive; `failed` when they did not (retryable). */
  status: "ok" | "loading" | "failed";
  onRetry: () => void;
  fixture: boolean;
  isLast: boolean;
  onPair: (p: PairKey) => void;
  onFlip: () => void;
  scale: number;
}) {
  const [ref, W] = useWidth(600);
  const { weeks, lo, hi, cursor, pin, hover, shown, ahead } = clock;
  const px = lane.kind === "px";
  // Taller lanes (owner, 2026-11-01): four of them now span about the left column.
  const H = Math.round((px ? 230 : 150) * scale) + (isLast ? 14 : 0);
  const Lx = 40;
  const Rx = W - 52;
  const T = 8;
  const B = isLast ? 20 : 6;
  const color = r(lane.color);
  const pairSeries = useMemo(() => weeks.map((w) => pairAt(w, lane.pair)), [weeks, lane.pair]);
  const hasData = !!values && values.some(Number.isFinite);
  const visHi = ahead === "hide" ? Math.min(hi, cursor) : hi;

  const x = (i: number) => Lx + ((i - lo) * (Rx - Lx)) / Math.max(1, hi - lo);
  const lowSrc = px && bars ? bars.low : (values ?? []);
  const highSrc = px && bars ? bars.high : (values ?? []);
  const lr = finiteRange(lowSrc, lo, visHi);
  const hr = finiteRange(highSrc, lo, visHi);
  let vlo = lr ? lr[0] : 0;
  let vhi = hr ? hr[1] : 1;
  const pad = (vhi - vlo) * 0.06 || 0.1;
  vlo -= pad;
  vhi += pad;
  const y = (v: number) => T + ((vhi - v) / (vhi - vlo)) * (H - T - B);
  const fmt = (v: number) => (!Number.isFinite(v) ? "—" : px ? v.toFixed(v < 100 ? 2 : 0) : v.toFixed(2) + "%");

  const pr = lane.pair !== "(none)" ? finiteRange(pairSeries, lo, visHi) : null;
  let plo = pr ? pr[0] : 0;
  let phi = pr ? pr[1] : 1;
  const pp = (phi - plo) * 0.06 || 0.05;
  plo -= pp;
  phi += pp;
  const py = (v: number) => {
    let t = (v - plo) / (phi - plo);
    if (lane.inverted) t = 1 - t;
    return T + (1 - t) * (H - T - B);
  };

  const n = hi - lo + 1;
  const cw = (Rx - Lx) / Math.max(1, n);
  const candles = px && !!bars && cw >= 3;
  const end = Math.min(cursor, hi);
  const cv = values?.[cursor] ?? NaN;
  const rho = values ? rho52(values, lane.kind, pairSeries, cursor) : null;
  const since = values ? sinceChange(values, lane.kind, pin, cursor) : null;

  const gridLevels = [0, 1, 2, 3].map((k) => vlo + ((vhi - vlo) * (k + 0.5)) / 4);

  return (
    <div data-testid={`yc-lane-${lane.key}`}>
      <div style={{ ...S.sphd, borderTop: "1px solid var(--qf-border)", flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1 }}>
          <b style={{ color }}>{lane.label}</b>
          {fixture && <Badge title="The macro series are not built on this server yet (or could not be read); drawn from fixture data">fixture</Badge>}
          <span style={{ color: "var(--qf-text-muted)" }}>vs</span>
          <select aria-label={`${lane.label} paired rate`} value={lane.pair} onChange={(e) => onPair(e.target.value as PairKey)} style={S.select}>
            {PAIRS.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <button
            type="button"
            title="Flip the paired rate for an inverse link"
            aria-pressed={lane.inverted}
            aria-label={`Flip ${lane.label}'s paired rate`}
            style={{ ...S.pill, padding: "0 6px", ...(lane.inverted ? { color: "var(--qf-accent)" } : null) }}
            onClick={onFlip}
          >
            ⇅
          </button>
          <span
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, minWidth: 0, color: "var(--qf-text-muted)", fontFamily: "inherit" }}
          >
            {lane.why}
          </span>
        </span>
        <span data-testid={`yc-lane-${lane.key}-read`}>
          <b>{fmt(cv)}</b>
          {since && (
            <>
              {" · 📌 "}
              <span
                style={{
                  color: since.unit === "bp" ? "var(--qf-text-muted)" : since.value >= 0 ? "var(--qf-positive)" : "var(--qf-negative)",
                }}
              >
                {since.unit === "%" ? `${signed(since.value, 1)}%` : `${since.value >= 0 ? "+" : ""}${Math.round(since.value)} bp`}
              </span>
            </>
          )}
          {" · "}
          <span title={`52-week correlation of weekly changes with ${lane.pair}`}>
            ρ52w{" "}
            {rho == null ? (
              "—"
            ) : (
              <b style={{ color: Math.abs(rho) > 0.4 ? "var(--qf-accent)" : "var(--qf-text-muted)" }}>{signed(rho)}</b>
            )}
          </span>
        </span>
      </div>
      <div ref={ref}>
        {!hasData ? (
          <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--qf-text-muted)", fontSize: 13 }}>
            {status === "loading" ? (
              "Loading bars…"
            ) : status === "failed" ? (
              <span>
                No bars for {lane.label}.{" "}
                <button type="button" style={S.pill} onClick={onRetry} aria-label={`Retry ${lane.label}`}>
                  ↻ Retry
                </button>
              </span>
            ) : (
              "No data for this chart."
            )}
          </div>
        ) : (
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} {...clock.link(Lx, Rx)}>
            {gridLevels.map((v, k) => (
              <g key={k}>
                <line x1={Lx} x2={Rx} y1={y(v)} y2={y(v)} stroke={r("grid")} />
                <text x={Rx + 5} y={y(v) + 3} style={txt(r)}>
                  {fmt(v)}
                </text>
              </g>
            ))}
            <TimeMarks r={r} clock={clock} x={x} top={T} bottom={H - B} labels={isLast} part="grid" />
            {pr && (
              <>
                {[plo + pp, phi - pp].map((v, k) => (
                  <text key={k} x={Lx - 4} y={py(v) + 3} textAnchor="end" style={{ ...txt(r), fontSize: 10 }}>
                    {v.toFixed(2)}
                  </text>
                ))}
                <path
                  d={linePath(pairSeries, lo, visHi, x, py)}
                  fill="none"
                  stroke={r("text.secondary")}
                  strokeOpacity={0.75}
                  strokeWidth={1.1}
                  strokeDasharray="4 3"
                />
              </>
            )}
            {candles && bars
              ? Array.from({ length: n }, (_, k) => lo + k).map((i) => {
                  if (!shown(i)) return null;
                  const o = bars.open[i]!;
                  const c = bars.close[i]!;
                  const h = bars.high[i]!;
                  const l = bars.low[i]!;
                  if (![o, c, h, l].every(Number.isFinite)) return null;
                  const fill = r(c >= o ? "positive" : "negative");
                  return (
                    <g key={i} opacity={i > cursor ? 0.2 : 1}>
                      <line x1={x(i)} x2={x(i)} y1={y(h)} y2={y(l)} stroke={fill} />
                      <rect
                        x={x(i) - cw * 0.33}
                        y={y(Math.max(o, c))}
                        width={cw * 0.66}
                        height={Math.max(1, Math.abs(y(o) - y(c)))}
                        fill={fill}
                      />
                    </g>
                  );
                })
              : values && (
                  <>
                    <path d={linePath(values, lo, end, x, y)} fill="none" stroke={color} strokeWidth={2.5} />
                    {ahead === "faint" && cursor < hi && (
                      <path d={linePath(values, Math.max(lo, cursor), hi, x, y)} fill="none" stroke={color} strokeOpacity={0.25} strokeWidth={2.5} />
                    )}
                  </>
                )}
            <TimeMarks r={r} clock={clock} x={x} top={T} bottom={H - B} labels={false} part="marks" />
            {Number.isFinite(cv) && (
              <g>
                <rect x={Rx + 1} y={y(cv) - 8} width={W - Rx - 2} height={16} rx={2} fill={color} />
                <text x={Rx + 5} y={y(cv) + 4} style={{ ...txt(r), fill: r("background"), fontWeight: 600 }}>
                  {px ? cv.toFixed(cv < 100 ? 2 : 0) : cv.toFixed(2)}
                </text>
              </g>
            )}
            {hover != null && hover >= lo && hover <= hi && shown(hover) && values && Number.isFinite(values[hover]!) && (
              <Bubble
                r={r}
                x={x(hover)}
                y={y(values[hover]!)}
                text={`${isoDay(weeks[hover]!.t)}  ${fmt(values[hover]!)}${pr ? `  ${lane.pair} ${pairSeries[hover]!.toFixed(2)}` : ""}`}
                color={color}
                right={Rx}
              />
            )}
          </svg>
        )}
      </div>
    </div>
  );
}

// ── Small parts ─────────────────────────────────────────────────────────────

function Seg({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: string;
  options: readonly (readonly [string, string])[];
  onPick: (v: string) => void;
}) {
  return (
    <div role="group" aria-label={label} style={S.seg}>
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={v === value} style={{ ...S.segBtn, ...(v === value ? S.segOn : null) }} onClick={() => onPick(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

function Badge({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span
      title={title}
      style={{
        fontFamily: MONO,
        fontSize: 10.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "1px 6px",
        borderRadius: 3,
        border: "1px solid currentColor",
        color: "var(--qf-warn)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Legend({ r }: { r: Resolve }) {
  const item = (token: string, text: string) => (
    <span key={text}>
      <i style={{ display: "inline-block", width: 14, height: 2, verticalAlign: "middle", marginRight: 5, background: r(token) }} />
      {text}
    </span>
  );
  return (
    <div style={S.legend}>
      {item("accent", "curve (normal)")}
      {item("negative", "curve (10Y < 2Y)")}
      {item("text.muted", "trail · last 8 weeks")}
      {item("text", "hovered week")}
      {item("warn", "pinned")}
      <span>◆ events (hover for the name)</span>
    </div>
  );
}

function txt(r: Resolve): CSSProperties {
  return { fontFamily: MONO, fontSize: 11, fill: r("text.muted") };
}

const S = {
  root: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 10,
    background: "var(--qf-surface)",
    color: "var(--qf-text)",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "6px 8px",
    background: "var(--qf-surface-alt)",
    border: "1px solid var(--qf-border)",
    borderRadius: 6,
  },
  grid: { display: "grid", gap: 14, alignItems: "start" },
  col: { display: "flex", flexDirection: "column", gap: 10, minWidth: 0 },
  box: { background: "var(--qf-background)", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" },
  pill: {
    fontFamily: MONO,
    fontSize: 11,
    padding: "1px 7px",
    borderRadius: 99,
    border: "1px solid var(--qf-border)",
    color: "var(--qf-text-secondary)",
    background: "none",
    cursor: "pointer",
  },
  lbl: { fontFamily: MONO, fontSize: 11.5, color: "var(--qf-text-muted)" },
  seg: { display: "flex", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" },
  segBtn: {
    padding: "3px 8px",
    fontFamily: MONO,
    fontSize: 11.5,
    color: "var(--qf-text-secondary)",
    background: "none",
    border: 0,
    cursor: "pointer",
  },
  segOn: { background: "var(--qf-selection)", color: "var(--qf-accent)" },
  sphd: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    padding: "4px 8px",
    fontFamily: MONO,
    fontSize: 12,
  },
  select: {
    background: "var(--qf-surface)",
    color: "var(--qf-text)",
    border: "1px solid var(--qf-border)",
    borderRadius: 4,
    fontFamily: MONO,
    fontSize: 12,
    padding: "1px 4px",
  },
  // One compact row: five cells share it, wrapping only when the column is narrow.
  tiles: { display: "flex", flexWrap: "wrap", gap: 6 },
  tile: {
    flex: "1 1 0",
    minWidth: 96,
    background: "var(--qf-background)",
    border: "1px solid var(--qf-border)",
    borderRadius: 5,
    padding: "4px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  tileK: { fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--qf-text-muted)" },
  tbl: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    fontFamily: MONO,
    fontSize: 10.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--qf-text-muted)",
    fontWeight: 500,
    textAlign: "left",
    padding: "5px 6px",
    borderBottom: "1px solid var(--qf-border)",
    whiteSpace: "nowrap",
  },
  td: { padding: "5px 6px", borderBottom: "1px solid var(--qf-border)" },
  tdR: {
    padding: "5px 6px",
    borderBottom: "1px solid var(--qf-border)",
    textAlign: "right",
    fontFamily: MONO,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  legend: { display: "flex", gap: 14, flexWrap: "wrap", fontFamily: MONO, fontSize: 11.5, color: "var(--qf-text-secondary)" },
} satisfies Record<string, CSSProperties>;
